import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismModal, PrismTable } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { t } from 'ember-intl';
import type { CurrencyConversionExchangeRatesProfile } from 'lumen/models';

import styles from './profile-currencies-modal.module.css';

interface HotelAndMarketCurrencyProfileCurrenciesModalSignature {
  Args: {
    onClose: () => void;
    profile: CurrencyConversionExchangeRatesProfile;
  };
}

export default class HotelAndMarketCurrencyProfileCurrenciesModal extends Component<HotelAndMarketCurrencyProfileCurrenciesModalSignature> {
  @service declare intl: Services['intl'];

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('general.currency'),
        valuePath: 'currency',
        textAlign: 'left' as const,
      },
      {
        name: this.intl.t('settings.currency_conversion.base_currency'),
        valuePath: 'baseCurrency',
        textAlign: 'left' as const,
      },
      {
        name: this.intl.t('settings.currency_conversion.exchange_rate'),
        valuePath: 'exchangeRate',
        textAlign: 'left' as const,
      },
    ];
  }

  get rows(): CustomEmberTableRow[] {
    const { profile } = this.args;
    const { baseCurrency } = profile;
    const baseUnit = `1 ${baseCurrency.code}`;

    return profile.rates
      .filter((rate) => rate.currency)
      .map((rate) => ({
        currency: `${rate.currency?.name} (${rate.currency?.symbol})`,
        baseCurrency: baseUnit,
        exchangeRate: `${rate.value} ${rate.currencyCode}`,
      }));
  }

  <template>
    <PrismModal
      @onClose={{@onClose}}
      @title={{t "currency-conversion.profile-currencies-modal.title"}}
      class={{styles.modal}}
    >
      <:default>
        <p class="text-300--regular text-neutral-subdued px-500 py-200">
          {{t
            "currency-conversion.profile-currencies-modal.selected_profile"
            name=@profile.title
          }}
        </p>

        <PrismTable
          @columns={{this.columns}}
          @removeTableBorder={{true}}
          @rows={{this.rows}}
        />
      </:default>
    </PrismModal>
  </template>
}
